/** Shared visual constants for the skill tree renderer (nodes, links, arrow markers). */

// Color constants
export const NODE_COLORS = {
    default: "#ffffff",
    /** Unselected nodes whose leaf has no linked markdown guide in `data.documents`. */
    noLinkedGuide: "#9ca3af",
    selected: "#20b2aa", // Teal for selected nodes
    hover: "#87ceeb", // Light teal for hover
    dragTarget: "#1e90ff", // Blue for drag-to-link target
    stroke: {
        default: "#000",
        selected: "#ffffff"
    }
} as const;

export const LINK_STYLES = {
    colors: {
        stroke: "#999", // White for main link stroke
        outline: "#000", // Black for link outline
        tempStroke: "#fff" // Black for temporary drag-to-link lines
    },
    strokeWidths: {
        outline: 6, // outline stroke width
        stroke: 4, // stroke width
        tempStroke: 4 // Temporary drag-to-link stroke width
    }
} as const;

export const ARROW_MARKER_STYLES = {
    /** Gray arrow drawn on the link-stroke layer (no stroke of its own). */
    id: "arrowhead",
    /** Black arrow silhouette drawn on the link-outline layer, slightly expanded via stroke. */
    outlineId: "arrowhead-outline",
    viewBox: "0 -8 16 16",
    /**
     * Anchor at the tail notch vertex (x=4 in the arrow path): the line terminates in the
     * notch and the arrow body alone covers the run to the tip. Anchoring at the tip (x=16)
     * leaves the 4px line wider than the tapering arrow near its point, blunting it.
     */
    refX: 4,
    refY: 0,
    markerWidth: 30,
    markerHeight: 30,
    orient: "auto",
    path: "M0,-8L16,0L0,8L4,0Z",
    colors: {
        fill: "#999", // Gray fill for arrow, matches link stroke
        stroke: "#000" // Black for the outline silhouette
    },
    /** In viewBox units; ~1px of rim at the 30/16 marker scale, matching the link outline. */
    outlineStrokeWidth: 1,
    /** Notch-to-tip distance in flow px: (16 - refX) / 16 viewBox units at the 30px marker size. */
    tipLength: 22.5
} as const;

// Node dimensions constants
export const NODE_DIMENSIONS = {
    width: 120,
    height: 64,
    radiusX: 60, // half of width
    radiusY: 32, // half of height
    hitTestRadius: 60 // for drag-to-link detection (React Flow connectionRadius)
} as const;

/** React Flow zoom bounds; animation scripts clamp their zoom targets to the same range. */
export const VIEWPORT_ZOOM_LIMITS = {
    min: 0.1,
    max: 4
} as const;

/**
 * Highlight pulse (animation-script highlightNode step): a glow in the script's color
 * that fades in/out `iterations` times. Keyframes live in main.css
 * (`skill-node-highlight-pulse`); the duration/count are applied inline from here.
 */
export const NODE_HIGHLIGHT_PULSE = {
    durationMs: 300,
    iterations: 2,
    /** How long the whole pulse takes; the highlightNode step resolves after this. */
    totalMs: 600
} as const;
